import React, { useState, useEffect, useContext, useRef } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { DowntimeSelector } from 'components/DowntimeSelector';
import { DowntimeReasonSelector } from 'components/DowntimeReasonSelector';

import { DowntimeSubmit } from 'components/DowntimeSubmit';
import { RefreshContext } from 'components/DataProvider';
import ErrorPage from 'pages/Error';
import { Loading } from 'components/Loading';
import MainLayout from 'components/Layouts/MainLayout';
import { toISO, now } from 'lib/utils/date';

import { useCategorization } from './useCategorization';
import { useAnnotationTypes } from './useAnnotationTypes';

const steps = {
  SELECT_DOWNTIME: 0,
  SELECT_REASON: 1,
  SUBMIT: 2,
};

const MultiStep = ({ node, setNode, preselected, ...props }) => {
  const { selectionResets, downtimes } = props;
  // A reset while this was unmounted (e.g. still loading) left nothing to submit.
  const wasCleared = !!selectionResets && _isEmpty(downtimes);
  // After a reset the operator picks from the list, so Back must lead there.
  const isPreselectedFlow = preselected && !selectionResets;
  const [step, setStep] = useState(
    preselected && !wasCleared ? steps.SELECT_REASON : steps.SELECT_DOWNTIME
  );
  // Only a reset made while this step is showing; it can remount mid-flow.
  const seenResetsRef = useRef(selectionResets);
  useEffect(() => {
    if (selectionResets !== seenResetsRef.current) {
      seenResetsRef.current = selectionResets;
      setStep(steps.SELECT_DOWNTIME);
    }
  }, [selectionResets]);
  const onBack = () => {
    return node.root ? previousStep() : setNode(node.parent);
  };
  const nextStep = () => {
    return setStep(step + 1);
  };
  const previousStep = () => {
    return setStep(step - 1);
  };
  const [filters, setFilters] = useState({ uncategorized: true, long: true });

  switch (step) {
    case steps.SELECT_DOWNTIME:
      return (
        <DowntimeSelector
          {...props}
          nextStep={nextStep}
          previousStep={previousStep}
          filters={filters}
          setFilters={setFilters}
        />
      );
    case steps.SELECT_REASON:
      return (
        <DowntimeReasonSelector
          {...props}
          node={node}
          onBack={isPreselectedFlow && node.root ? null : onBack}
          onNext={() => {
            return nextStep();
          }}
          onSelect={(child) => {
            const { children, data: annotationType } = child;

            if (children.length) {
              props.dispatch({ type: 'UNSET_ANNOTATION_TYPE' });
              setNode(child);
            } else {
              props.dispatch({
                type: 'SET_ANNOTATION_TYPE',
                annotationType,
              });
              nextStep();
            }
          }}
        />
      );
    case steps.SUBMIT:
      return (
        <DowntimeSubmit
          {...props}
          nextStep={nextStep}
          previousStep={previousStep}
        />
      );
    default:
      return (
        <ErrorPage
          error={new Error('Invalid step in categorize downtime flow')}
        />
      );
  }
};

const CategorizePage = ({ location }) => {
  const { preselected, splitting: shouldSplit, forced } = _get(
    location,
    'state',
    false
  );

  const returnPath = _get(location, ['state', 'returnPath'], '/');

  const { resyncForCategorize } = useContext(RefreshContext);
  const pendingResyncRef = useRef(null);
  useEffect(() => {
    pendingResyncRef.current = resyncForCategorize();
    // Only when the screen opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading: categorizationLoading } = useCategorization({
    returnPath,
    waitForResync: () => {
      return pendingResyncRef.current;
    },
  });

  const { loading, node, setNode } = useAnnotationTypes();

  const { dispatch, splitting } = data;

  useEffect(() => {
    if (preselected) {
      if (shouldSplit) {
        const splitTimestamp = _get(
          location,
          ['state', 'splitTimestamp'],
          toISO(now())
        );
        dispatch({
          type: 'SELECT_DOWNTIME_TO_SPLIT',
          downtime: preselected,
          splitTimestamp,
        });
      } else {
        dispatch({
          type: 'TOGGLE_DOWNTIME',
          downtime: preselected,
        });
      }
    }
  }, [dispatch, preselected, shouldSplit, location]);

  if (loading || categorizationLoading) return <Loading />;

  return (
    <MainLayout.Container
      id="categorize-downtime-flow"
      direction="column"
      overlay
    >
      <MultiStep
        {...data}
        node={node}
        setNode={setNode}
        preselected={preselected}
        splitting={splitting}
        forced={forced}
        returnPath={returnPath}
      />
    </MainLayout.Container>
  );
};

export default CategorizePage;
